import { Box, Text } from '@chakra-ui/react';
import { KeyMetricCard } from 'src/components/molecules/KeyMetricCard';
import { KeyMetricsGrid } from 'src/components/molecules/KeyMetricsGrid';
import { ApiForecast } from 'src/api/queries';
import {
  TrendResult,
  calculateTrendFromComparison,
} from 'src/utils/calculateTrendFromTimeSeries';
// eslint-disable-next-line no-restricted-imports
import { Icon as ChakraIcon } from '@chakra-ui/react';
import { BarChart3, CircleDollarSign, TrendingUp } from 'lucide-react';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import { useKpiUnit } from 'src/hooks/useKpiUnit';

type ForecastMetricsProps = {
  forecast: ApiForecast;
};

const trendTooltip =
  'Shows how much the primary scenario has increased or decreased compared to the comparison scenario.';

export const ForecastMetrics: React.FC<ForecastMetricsProps> = ({
  forecast,
}) => {
  const { metricLabel } = useKpiMetricLabels();
  const { isEuro } = useKpiUnit();

  if (forecast.scenarios.length === 0) return null;

  // Use first scenario for primary metrics, second for comparison (if exists)
  const primary = forecast.scenarios[0];
  const comparison =
    forecast.scenarios.length > 1 ? forecast.scenarios[1] : undefined;

  const calcTotals = (scenario: typeof primary) => {
    const totalSales = scenario.results.reduce(
      (s, r) => s + r.predictedSales,
      0,
    );
    const totalBaseline = scenario.results.reduce((s, r) => s + r.baseline, 0);
    const totalSpend = scenario.results.reduce((s, r) => s + r.totalSpend, 0);
    const incrementalSales = totalSales - totalBaseline;
    const roi = totalSpend > 0 ? incrementalSales / totalSpend : 0;
    return { totalSales, totalSpend, roi, incrementalSales };
  };

  const p = calcTotals(primary);
  const c = comparison ? calcTotals(comparison) : undefined;

  // Trend vs the comparison scenario; without one the cards show plain values
  const trendOf = (value: number, baseline?: number): Partial<TrendResult> =>
    baseline === undefined ? {} : calculateTrendFromComparison(value, baseline);

  // Compute date range from results
  const allDates = primary.results.map((r) => r.periodDate).sort();
  const dateRange =
    allDates.length > 0
      ? `${forecast.periods} ${forecast.frequency} periods`
      : '';

  return (
    <Box mb={4}>
      <KeyMetricsGrid columns={3}>
        <KeyMetricCard
          title={`Predicted ${metricLabel}`}
          isEuro={isEuro}
          value={Math.round(p.totalSales)}
          {...trendOf(p.totalSales, c?.totalSales)}
          trendTooltip={trendTooltip}
          baselineValue={c ? Math.round(c.totalSales) : undefined}
          icon={<ChakraIcon as={TrendingUp} boxSize={5} color="gray.500" />}
        />
        <KeyMetricCard
          title="Total Spend"
          isEuro
          value={Math.round(p.totalSpend)}
          {...trendOf(p.totalSpend, c?.totalSpend)}
          trendTooltip={trendTooltip}
          baselineValue={c ? Math.round(c.totalSpend) : undefined}
          icon={
            <ChakraIcon as={CircleDollarSign} boxSize={5} color="gray.500" />
          }
        />
        <KeyMetricCard
          title="Incremental ROI"
          value={Number(p.roi.toFixed(2))}
          {...trendOf(p.roi, c?.roi)}
          trendTooltip={trendTooltip}
          baselineValue={c ? Number(c.roi.toFixed(2)) : undefined}
          icon={<ChakraIcon as={BarChart3} boxSize={5} color="gray.500" />}
        />
      </KeyMetricsGrid>
      {dateRange && (
        <Text
          fontSize="xs"
          fontFamily="marketingPage"
          color="text.light.secondary">
          over {dateRange}
        </Text>
      )}
    </Box>
  );
};
